import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Suspense } from "react";
import { BrowserRouter } from "react-router";
import { logout } from "@/features/auth/store/sessionSlice";
import UnpaidInvoicesGate from "@/features/organization-settings/subFeatures/direct-debit/components/unpaid/UnpaidInvoicesGate";
import ErrorFallback from "@/shared/components/ErrorFallback";
import { OldBrowserAlert } from "@/shared/components/OldBrowserAlert";
import AppProvider from "@/shared/contexts/AppProvider";
import { ThemeProvider } from "@/shared/contexts/ThemeProvider";
import { useVersionCheck } from "@/shared/hooks/useVersionCheck";
import { queryClient } from "@/shared/lib/queryClient";
import { Sentry } from "@/shared/services/sentry.service";
import { Toaster } from "@/shared/ui/Sonner";
import { lazyWithRetry } from "@/shared/utils/lazyWithRetry";
import { store } from "@/store";
import AppRouter from "./AppRouter";
import BridgeTransferListener from "./BridgeTransferListener";
import UsageTrackingProvider from "./usage-tracking/UsageTrackingProvider";

// Only opened from the unpaid gate: its form and upload stack stays out of the entry chunk.
const CreateTicketModal = lazyWithRetry(() => import("@/features/helpdesk/components/create-ticket/CreateTicketModal").then((m) => ({ default: m.CreateTicketModal })));

const VersionChecker = () => {
  useVersionCheck();
  return null;
};

/**
 * Main application component with all providers
 * Separates provider setup from DOM rendering (main.tsx)
 */
export const App = () => {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AppProvider>
              <OldBrowserAlert />
              <VersionChecker />
              <BridgeTransferListener />
              <UsageTrackingProvider>
                <UnpaidInvoicesGate
                  renderSupport={(props) => (
                    <Suspense fallback={null}>
                      <CreateTicketModal {...props} />
                    </Suspense>
                  )}
                  // App renders above the Redux Provider, so useDispatch is unavailable here.
                  onSignOut={() => store.dispatch(logout({ isVoluntary: true }))}
                >
                  <AppRouter />
                </UnpaidInvoicesGate>
              </UsageTrackingProvider>
              <Toaster richColors />
            </AppProvider>
          </BrowserRouter>
          {import.meta.env.DEV && import.meta.env.VITE_RQ_DEVTOOLS !== "false" && (
            <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
          )}
        </QueryClientProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  );
};

export default App;
